import * as THREE from "three";

export interface ChaseCameraOptions {
  distance: number;
  height: number;
  lookAtHeight: number;
  /**
   * How far the look-at focal point is allowed to drift from the board
   * center toward the current move (0 = camera stays centered on the
   * board regardless of where the knight is; 1 = old tight chase-cam
   * behavior, centered purely on the move). Keeping this low is what
   * keeps the whole 8×8 board in frame instead of cropping edges near the
   * knight's current position.
   */
  focusBlend: number;
}

const BOARD_CENTER = new THREE.Vector3(0, 0, 0);

// Pulled back and raised well beyond the board's ~7-unit span so the full
// board stays in frame through a move, not just a tight shot of the
// knight — see `focusBlend` above for why the look-at target doesn't
// simply track the knight either.
const DEFAULT_OPTIONS: ChaseCameraOptions = { distance: 7.5, height: 6.5, lookAtHeight: 0.15, focusBlend: 0.4 };

export interface CameraTarget {
  position: THREE.Vector3;
  lookAt: THREE.Vector3;
}

/**
 * Trailing-diagonal follow position behind the knight's direction of
 * travel (section 20), pulled back far enough to keep the whole board
 * visible. `focusT` shifts the *move's own* focal point from the midpoint
 * (0) toward the destination square (1) as a jump progresses; that move
 * focal point is then blended with the board center by `focusBlend` so
 * the camera leans toward the action without losing the far corners.
 */
export function computeChaseCameraTarget(
  start: THREE.Vector3,
  end: THREE.Vector3,
  focusT: number,
  options: ChaseCameraOptions = DEFAULT_OPTIONS
): CameraTarget {
  const direction = new THREE.Vector3().subVectors(end, start);
  if (direction.lengthSq() < 1e-6) direction.set(0, 0, 1);
  direction.normalize();

  const midpoint = new THREE.Vector3().lerpVectors(start, end, 0.5);
  const moveFocal = midpoint.lerp(end, Math.min(Math.max(focusT, 0), 1));
  const focal = BOARD_CENTER.clone().lerp(moveFocal, options.focusBlend);

  const position = focal
    .clone()
    .sub(direction.clone().multiplyScalar(options.distance))
    .add(new THREE.Vector3(0, options.height, 0));

  const lookAt = focal.clone().add(new THREE.Vector3(0, options.lookAtHeight, 0));
  return { position, lookAt };
}

/** Wide, settled composition used while DISPLAYING_COMPLETION holds the finished board. */
export function computeWideCompletionTarget(boardCenter: THREE.Vector3): CameraTarget {
  return {
    position: boardCenter.clone().add(new THREE.Vector3(0, 6.5, 7.5)),
    lookAt: boardCenter.clone(),
  };
}
